import { Trans } from '@lingui/macro';
import { Menu, Users, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useHistory } from '../../../hooks/useRouterCompat';
import { useAppUIContext } from '../../../context/AppUIContext';
import { useMyPermissions } from '../../../context/WorkspacePermissionContext';
import { Soribot } from '../../../models/Soribot';
import { Fire } from '../../../services/FireClient';
import { SoribotItem } from '../../soribots/MySoribotsPage';
import SoribotCreateModal from '../../soribots/SoribotCreateModal';
import PremiumWSIcon from '../../components/PremiumWSIcon';
import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';

const WSAdminBotsPage: React.FC = () => {
  const { workspaces } = useAppUIContext();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const workspace = workspaces.find(w => w.id === workspaceId);
  const [inProgress, setInProgress] = useState(false);
  const [bots, setBots] = useState<Soribot[]>([]);
  const history = useHistory();
  const perms = useMyPermissions();

  useEffect(() => {
    setInProgress(true);
    Fire.bot.getWorkspaceBots(workspaceId || "").then(list => {
      setBots(list.sort((a, b) => a.name?.localeCompare(b.name)));
      setInProgress(false);
    });
  }, [workspaceId]);

  return (
    <div className="flex flex-col h-screen bg-muted/50">
      <header className="flex items-center px-4 h-14 border-b bg-background shrink-0 gap-2">
        <Button variant="ghost" size="icon" onClick={() => history.goBack()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        {/* Intentionally keeping Menu button visual if needed, but using Back button for nav structure consistency for now */}
        {/* <Button variant="ghost" size="icon"><Menu className="w-5 h-5" /></Button> */}

        <div className="flex flex-col">
          <h1 className="text-sm font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" />
            {workspace?.name}
          </h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>id: {workspace?.id}</span>
            <PremiumWSIcon ws={workspace} />
          </div>
        </div>
      </header>

      {inProgress && <Progress value={undefined} className="h-1 absolute top-14 left-0 right-0 z-10" />}

      <div className="flex-1 overflow-y-auto p-4 w-full max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-medium text-foreground"><Trans>Soribots in workspace</Trans></h2>
          {perms?.canManageBots !== false && <SoribotCreateModal workspaceId={workspaceId || null} />}
        </div>

        {bots.length === 0 && !inProgress && (
          <div className="text-center py-10 text-muted-foreground">
            <Trans>No bots</Trans>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {bots.map(b => SoribotItem(b, workspace))}
        </div>
      </div>
    </div>
  );
};

export default WSAdminBotsPage;
